import * as React from "react";
import { useSelector } from "react-redux";
import { Team, User } from "~/normalizr/entities";
import { buildMentionName } from "~/Redux/AvailableUsers/mentions/utils";
import { getClientIsSpacesEnabled } from "~/Redux/Customization";
import { useSuggestions } from "~/sharedComponents/advancedEditor/hooks/useSuggestions";
import { Mention } from "~/sharedComponents/advancedEditor/types/types";
import {
    getSortedByName,
    getSortedUsers,
} from "~/sharedComponents/select/multiSelect/userSelect/utils/resourcesSorting";
import { MentionSuggestionItem } from "..";
import { suggestionsFilterWithMembers } from "../utils/suggestionsFilterWithMembers";

const useMentions = (availableMentions: User[], teams: Team[]) => {
    const users = React.useMemo(() => {
        if (!availableMentions.length) {
            /*
             *  For rare cases when available suggestions was not loaded yet but user
             *  already typed @ (or click "@" button). In this case popup won't be opened if
             *  no suggestions will be passed to MentionSuggestions component. And it won't be shown
             *  even when available suggestions are loaded because we don't change suggestions when
             *  available mentions are updated but popup is not opened (see comment in useSuggestions).
             *  So to apply available mentions after initial loading add current user to available mentions.
             */
            return [{ UserId: ep.settings.currentUserId, Name: ep.settings.currentUserName }];
        }

        return availableMentions;
    }, [availableMentions]);

    const items: Mention[] = React.useMemo(() => {
        const teamsItems = getSortedByName(teams).map(user => ({
            name: buildMentionName(user.Name),
            teamId: user.TeamId,
            users: user.Users,
            title: user.Name,
        }));

        const userItems = getSortedUsers(users).map(user => ({
            name: buildMentionName(user.Name),
            id: user.UserId,
            title: user.Name,
        }));

        return [...userItems, ...teamsItems];
    }, [users, teams]);

    return items;
};

export const useSuggestionsFroMessageBoard = (
    availableMentions: User[],
    isOpenedRef: React.MutableRefObject<boolean>,
    projectMembers: number[],
    spaceMembers: number[],
    teams: Team[]
) => {
    const isSpacesEnabled = useSelector(getClientIsSpacesEnabled);
    const mentions = useMentions(availableMentions, teams);
    const suggestionsFilter = React.useCallback(
        (searchValue: string, suggestions: []) =>
            suggestionsFilterWithMembers({
                searchValue,
                suggestions,
                projectMembers,
                spaceMembers,
                isSpacesEnabled,
            }),
        [projectMembers, spaceMembers, isSpacesEnabled]
    );
    const { suggestions, onSearchChange } = useSuggestions<MentionSuggestionItem>(
        mentions,
        isOpenedRef,
        suggestionsFilter
    );

    return {
        suggestions,
        onSearchChange,
    } as const;
};
